import type { GenerationFailureCode, GenerationFailureDto } from '@/types/api-generated'

/**
 * Who can do something about a failure, which decides its colour and whether Retry is offered.
 * `ready` is the person's fix having landed, shown in the same place until they build again.
 */
export type GenerationFailureKind = 'person' | 'retry' | 'ours' | 'unknown' | 'ready'

export interface GenerationFailureView {
    code: GenerationFailureCode
    kind: GenerationFailureKind
    title: string
    message: string
    /** One line for Webster's chat, written as Webster. */
    chatLine: string
    /** Retry is pointless when it is on our side or needs the person to change something first. */
    offerRetry: boolean
    reference: string | null
    /** Set for FontUnavailable. */
    font: { family: string; fromBrandGuidelines: boolean } | null
    /** Set for TemplateMissingBodySlot, when the server knew which template. */
    templateAssetId: string | null
}

const GENERIC_PAGE = 'Webster could not finish this page. Retry generation. If it fails again, contact support.'
const GENERIC_EMAIL = 'Webster could not finish this email. Retry generation. If it fails again, contact support.'

// The one sentence the server sent before it sent codes. A stored GenerationError from then reads as its code.
const LEGACY_TOOLS_UNAVAILABLE = "Webster's design tools are temporarily unavailable. Please try again in a little bit."

const KNOWN_CODES: readonly GenerationFailureCode[] = [
    'Unknown', 'FontUnavailable', 'TemplateMissingBodySlot', 'WebsterTimedOut', 'WebsterStepLimit',
    'WebsterReplyUnreadable', 'FormPlaceholderMissing', 'DesignToolsUnavailable', 'AiUsageLimitReached',
    'PageMissingAfterRebuild', 'EmailTemplateNotBound', 'TemplateUnavailable',
]

/**
 * Turns what the server stored or sent into a failure. Accepts the structured failure, a bare code
 * (the SignalR event), or a legacy message; anything unrecognised is Unknown, so no internal text
 * ever becomes the explanation.
 */
export function toGenerationFailure(source: GenerationFailureDto | string | null | undefined): GenerationFailureDto | null {
    if (source == null || source === '') return null
    if (typeof source !== 'string') return source
    if ((KNOWN_CODES as readonly string[]).includes(source)) return { Code: source, Parameters: {} }
    if (source === LEGACY_TOOLS_UNAVAILABLE) return { Code: 'DesignToolsUnavailable', Parameters: {} }
    return { Code: 'Unknown', Parameters: {} }
}

const RETRY_MESSAGE = 'This happens now and then, and a retry usually works.'
const OURS_MESSAGE = "This is a problem on our side, not with your {subject}, and retrying won't help until we fix it. Contact OrbitalX support and quote the reference below."

export function describeGenerationFailure(
    source: GenerationFailureDto | string | null | undefined,
    subject: 'page' | 'email' | 'template' = 'page',
): GenerationFailureView {
    const failure = toGenerationFailure(source) ?? { Code: 'Unknown', Parameters: {} }
    const code = ((KNOWN_CODES as readonly string[]).includes(failure.Code) ? failure.Code : 'Unknown') as GenerationFailureCode
    const parameters = failure.Parameters ?? {}
    const base = {
        code,
        reference: failure.Reference ?? null,
        font: null,
        templateAssetId: null,
    }
    const retry = (title: string, chatLine: string): GenerationFailureView => ({
        ...base, kind: 'retry', title, message: RETRY_MESSAGE, chatLine, offerRetry: true,
    })
    const ours = (title: string): GenerationFailureView => ({
        ...base, kind: 'ours', title, message: OURS_MESSAGE.replace('{subject}', subject), offerRetry: false,
        chatLine: `I can't build this ${subject} right now because of a problem on OrbitalX's side.`,
    })

    switch (code) {
        case 'FontUnavailable': {
            const family = parameters.Font?.trim()
            if (!family) break
            const fromBrandGuidelines = parameters.Source !== 'Page'
            return {
                ...base,
                kind: 'person',
                title: `${family} isn't available to Webster`,
                message: fromBrandGuidelines
                    ? "Your brand font isn't a Google Font, and no font file has been uploaded for this page. Webster stopped rather than build the page in a different font."
                    : "The font chosen for this page isn't a Google Font, and no font file has been uploaded for it.",
                chatLine: fromBrandGuidelines
                    ? `I couldn't build this page: your brand font ${family} isn't a Google Font and hasn't been uploaded. Upload it in Typography or choose a font for this page, and I'll start again.`
                    : `I couldn't build this page: ${family} isn't a Google Font and hasn't been uploaded. Upload it in Typography or choose another font, and I'll start again.`,
                offerRetry: false,
                font: { family, fromBrandGuidelines },
            }
        }
        case 'TemplateMissingBodySlot':
            return {
                ...base,
                kind: 'person',
                title: 'This template has no place for the email body',
                message: 'The template bound to this email is missing the body slot Webster writes into, so the copy has nowhere to go. Regenerate the template, or use a different one for this email.',
                chatLine: "I couldn't write this email: its template has no body slot. Fix the template or choose another one and I'll try again.",
                offerRetry: false,
                templateAssetId: parameters.TemplateAssetId ?? null,
            }
        case 'EmailTemplateNotBound':
            return {
                ...base,
                kind: 'person',
                title: 'This email has no template yet',
                message: 'Webster writes the copy into the template bound to this email, and none is bound, so there was nowhere to put it.',
                chatLine: "I couldn't write this email: it has no template yet. Choose one in the Template field and I'll try again.",
                offerRetry: false,
            }
        case 'TemplateUnavailable': {
            // A page started from a landing page template (ORB-1103) whose template can no longer be copied. Retry copies
            // it again, so it can't help; deleting the landing page asset frees the web to be built another way.
            const name = parameters.TemplateName?.trim()
            const template = name ? `the template “${name}”` : 'its template'
            return {
                ...base,
                kind: 'person',
                title: name ? `“${name}” can no longer be copied` : 'The template can no longer be copied',
                message: `This page was started from ${template}, and the signed-off version it copied is gone, so Webster can't start it again. Delete this landing page in the web's Assets tab, then build it again from another template or from scratch.`,
                chatLine: `I can't start this page again: the version of ${template} it was copied from is gone. Delete this landing page in the web's Assets tab, then build it again from another template or from scratch.`,
                offerRetry: false,
            }
        }
        case 'WebsterTimedOut':
            return retry(`Webster ran out of time on this ${subject}`, `I ran out of time on this ${subject}. Retrying usually works.`)
        case 'WebsterStepLimit':
            return retry(`Webster took too many steps on this ${subject}`, `I took too many steps on this ${subject}. Retrying usually works.`)
        case 'WebsterReplyUnreadable':
            return retry("Webster's reply came back in the wrong shape", 'My reply came back in the wrong shape. Retrying usually works.')
        case 'FormPlaceholderMissing':
            return retry('Webster left out the registration form', 'I left out the registration form. Retrying usually works.')
        case 'DesignToolsUnavailable':
            return {
                ...base, kind: 'retry', title: "Webster's design tools are unavailable",
                message: LEGACY_TOOLS_UNAVAILABLE, chatLine: LEGACY_TOOLS_UNAVAILABLE, offerRetry: true,
            }
        case 'AiUsageLimitReached':
            return ours('Webster is unavailable right now')
        case 'PageMissingAfterRebuild':
            return ours('Webster lost track of this page')
        default:
            break
    }

    const generic = subject === 'page' ? GENERIC_PAGE : GENERIC_EMAIL.replace('email', subject)
    return { ...base, code: 'Unknown', kind: 'unknown', title: 'Generation failed', message: generic, chatLine: generic, offerRetry: true }
}

const REPEAT_SUFFIX = / \((\d+) attempts\)$/

/** The chat text for a failure that may already be the last line: one line with a count, not one line per retry. */
export function repeatedChatLine(previous: string | null | undefined, line: string): string | null {
    if (!previous) return null
    const match = previous.match(REPEAT_SUFFIX)
    const previousBase = match ? previous.slice(0, match.index) : previous
    if (previousBase !== line) return null
    const attempts = match ? Number(match[1]) + 1 : 2
    return `${line} (${attempts} attempts)`
}

/** Whether a copy's failure is one the person fixes in its Template field. */
export function isFixedInTemplateField(view: GenerationFailureView): boolean {
    return view.code === 'EmailTemplateNotBound' || view.code === 'TemplateMissingBodySlot'
}

/**
 * Whether such a failure still describes the template chosen in that field. Once another is picked, it is about
 * one the copy no longer uses, and the next build says what happens with the new one.
 */
export function stillDescribesChosenTemplate(view: GenerationFailureView, boundTemplateId: string | null): boolean {
    if (view.code === 'EmailTemplateNotBound') return !boundTemplateId
    return !view.templateAssetId || view.templateAssetId === boundTemplateId
}
